import React, { useEffect, useMemo, useState } from 'react';
import { Check, Copy, Loader2, Play, RotateCcw, Search, ShieldCheck, ShieldX, Table2 } from 'lucide-react';
import { DialogShell, fieldClass, ghostBtn, labelClass, primaryBtn } from '../components/dataModelsUi';
import { runPolicyTest, type PolicyTestResult, type PolicyTestStep } from '../policyTestApi';
import { buildAssetSelectSql, formatPreviewCell } from '../tablePreviewApi';
import { resolveDirectoryUserId } from './policyDirectory';
import { claimsForUser, identityFromClaims, userOptionLabel } from './policyTestClaims';
import { userById } from './queryTest';
import type { GovernancePolicy, PolicyAsset } from './types';
import { POLICY_TYPE_LABEL } from './types';
import { assetFieldLabel } from './usePolicyAssetColumns';
import { usePolicyDirectory } from './usePolicyDirectory';

type ResultPane = 'trace' | 'queries' | 'rows';

function stepStage(id: string): 'Compile' | 'Rewrite' | 'Result' {
  if (id === 'original' || id === 'bindings' || id === 'filters' || id === 'masks' || id === 'rewritten') return 'Rewrite';
  if (id === 'decision' || id === 'result') return 'Result';
  return 'Compile';
}

function CopyButton({
  isLight,
  value,
  label,
}: {
  isLight: boolean;
  value: string;
  label: string;
}) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      className={`${ghostBtn(isLight)} !h-7 !px-2 !text-[11px]`}
      onClick={() => {
        void navigator.clipboard?.writeText(value);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1400);
      }}
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
      {copied ? 'Copied' : label}
    </button>
  );
}

function Metric({
  isLight,
  label,
  value,
}: {
  isLight: boolean;
  label: string;
  value: string;
}) {
  return (
    <div className={`rounded-md border px-2.5 py-1.5 ${isLight ? 'border-[#E6E8EB] bg-white' : 'border-white/10 bg-white/[0.03]'}`}>
      <p className={`text-[10px] font-medium uppercase tracking-wide ${isLight ? 'text-[#6B7280]' : 'text-slate-400'}`}>{label}</p>
      <p className={`mt-0.5 text-[12px] font-medium ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{value}</p>
    </div>
  );
}

function SqlBlock({
  isLight,
  title,
  value,
}: {
  isLight: boolean;
  title: string;
  value: string;
}) {
  return (
    <div className={`flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border ${isLight ? 'border-[#E6E8EB] bg-[#F7F8FA]' : 'border-white/10 bg-white/[0.03]'}`}>
      <div className={`flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2 ${isLight ? 'border-[#E6E8EB]' : 'border-white/10'}`}>
        <p className={`text-[12px] font-medium ${isLight ? 'text-[#1C1C1C]' : 'text-white'}`}>{title}</p>
        <CopyButton isLight={isLight} value={value} label="Copy" />
      </div>
      <pre className={`min-h-0 flex-1 overflow-auto px-3 py-2 font-mono text-[11px] leading-5 ${isLight ? 'text-[#3C4048]' : 'text-slate-300'}`}>
        {value || '—'}
      </pre>
    </div>
  );
}

export default function PolicyTestDialog({
  isLight,
  policy,
  policyId,
  assets,
  initialUserId,
  onClose,
}: {
  isLight: boolean;
  policy: GovernancePolicy;
  policyId: string;
  assets: PolicyAsset[];
  initialUserId?: string;
  onClose: () => void;
}) {
  const directory = usePolicyDirectory();
  const [userId, setUserId] = useState(initialUserId || '');
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [claimQuery, setClaimQuery] = useState('');
  const [assetId, setAssetId] = useState(assets[0]?.id || '');
  const [query, setQuery] = useState(() => buildAssetSelectSql(assets[0]?.name || '', assets[0]?.path));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<PolicyTestResult | null>(null);
  const [pane, setPane] = useState<ResultPane>('trace');
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const ink = isLight ? 'text-[#1C1C1C]' : 'text-white';
  const line = isLight ? 'border-[#E6E8EB]' : 'border-white/10';
  const rail = isLight ? 'bg-[#F7F8FA]' : 'bg-white/[0.02]';
  const user = userById(userId, directory.simUsers);
  const claims = useMemo(() => claimsForUser(user, overrides), [overrides, user]);
  const selected = assets.find((asset) => asset.id === assetId) ?? assets[0];
  const fakedCount = claims.filter((claim) => claim.faked).length;
  const visibleClaims = useMemo(() => {
    const needle = claimQuery.trim().toLowerCase();
    if (!needle) return claims;
    return claims.filter((claim) => `${claim.token} ${claim.value} ${claim.description}`.toLowerCase().includes(needle));
  }, [claimQuery, claims]);

  useEffect(() => {
    setUserId((current) => resolveDirectoryUserId(current, directory.simUsers));
  }, [directory.simUsers]);

  const chooseAsset = (id: string) => {
    const next = assets.find((asset) => asset.id === id);
    setAssetId(id);
    if (next) setQuery(buildAssetSelectSql(next.name, next.path));
  };

  const run = async () => {
    if (!user || !selected || busy) return;
    setBusy(true);
    setError('');
    try {
      const next = await runPolicyTest({
        policyId,
        assetId: selected.id,
        query: query.trim(),
        testIdentity: identityFromClaims(user, claims),
      });
      setResult(next);
      setPane(next.allow ? 'rows' : 'trace');
    } catch (err) {
      setResult(null);
      setError(err instanceof Error ? err.message : 'Unable to run policy test');
    } finally {
      setBusy(false);
    }
  };

  const resultColumns = result?.columns.length
    ? result.columns
    : Object.keys(result?.rows[0] || {}).map((name) => ({ name, type: '' }));

  return (
    <DialogShell
      title="Policy test"
      subtitle={`${policy.name || 'Untitled'} · ${POLICY_TYPE_LABEL[policy.type]}`}
      isLight={isLight}
      onClose={onClose}
      size="page"
      footer={(
        <>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>Close</button>
          <button
            type="button"
            className={primaryBtn()}
            disabled={!user || !selected || !query.trim() || busy}
            onClick={() => void run()}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            Run test
          </button>
        </>
      )}
    >
      <div className="flex h-full min-h-0 flex-col overflow-hidden lg:flex-row">
        <aside className={`flex max-h-[40vh] min-h-0 w-full shrink-0 flex-col border-b lg:max-h-none lg:w-[20.5rem] lg:border-b-0 lg:border-r ${line} ${rail}`}>
          <div className={`shrink-0 space-y-3 border-b px-4 py-3 ${line}`}>
            <div>
              <label className={labelClass(isLight)}>Run as</label>
              <select
                className={fieldClass(isLight)}
                value={userId}
                onChange={(event) => {
                  setUserId(event.target.value);
                  setOverrides({});
                }}
                disabled={!directory.simUsers.length}
                aria-label="Test as user"
              >
                {!directory.simUsers.length ? <option value="">{directory.loading ? 'Loading…' : 'No people'}</option> : null}
                {directory.simUsers.map((item) => (
                  <option key={item.id} value={item.id}>{userOptionLabel(item)}</option>
                ))}
              </select>
              {user ? (
                <p className={`mt-1.5 truncate text-[11px] ${muted}`}>
                  {[user.email, user.department || user.role].filter(Boolean).join(' · ') || user.id}
                </p>
              ) : null}
            </div>
            <div>
              <label className={labelClass(isLight)}>Table</label>
              <select
                className={fieldClass(isLight)}
                value={selected?.id || ''}
                onChange={(event) => chooseAsset(event.target.value)}
                disabled={!assets.length}
              >
                {!assets.length ? <option value="">Select a table in Applies to</option> : null}
                {assets.map((asset) => (
                  <option key={asset.id} value={asset.id}>{assetFieldLabel(asset)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className={`flex shrink-0 items-center justify-between gap-2 px-4 pb-2 pt-3`}>
            <div>
              <p className={`text-[13px] font-medium ${ink}`}>Claims</p>
              <p className={`text-[11px] ${muted}`}>{fakedCount ? `${fakedCount} edited` : 'Directory values'}</p>
            </div>
            {fakedCount ? (
              <button type="button" className={`${ghostBtn(isLight)} !h-7 !px-2 !text-[11px]`} onClick={() => setOverrides({})}>
                <RotateCcw size={12} />
                Reset
              </button>
            ) : null}
          </div>
          <div className="shrink-0 px-4 pb-2">
            <div className="relative">
              <Search size={13} className={`pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 ${muted}`} />
              <input
                className={`${fieldClass(isLight)} !py-1.5 pl-8 text-[12px]`}
                value={claimQuery}
                onChange={(event) => setClaimQuery(event.target.value)}
                placeholder="Search claims"
                aria-label="Search claims"
              />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
            {visibleClaims.map((claim) => (
              <label key={claim.path} className={`mb-1.5 block rounded-md border px-2.5 py-2 ${line} ${isLight ? 'bg-white' : 'bg-black'}`}>
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span className={`truncate font-mono text-[11px] ${isLight ? 'text-[#1B6CA8]' : 'text-sky-300'}`} title={claim.description}>{claim.token}</span>
                  {claim.faked ? (
                    <button
                      type="button"
                      className={`shrink-0 text-[10px] font-medium ${isLight ? 'text-[#1B6CA8]' : 'text-sky-300'}`}
                      onClick={(event) => {
                        event.preventDefault();
                        setOverrides((current) => {
                          const next = { ...current };
                          delete next[claim.path];
                          return next;
                        });
                      }}
                    >
                      Undo
                    </button>
                  ) : (
                    <span className={`shrink-0 text-[10px] ${muted}`}>{claim.type}</span>
                  )}
                </div>
                <input
                  className={`${fieldClass(isLight)} !py-1 font-mono text-[11px]`}
                  value={claim.value}
                  onChange={(event) => setOverrides((current) => ({ ...current, [claim.path]: event.target.value }))}
                />
              </label>
            ))}
            {!claims.length ? <p className={`px-1 py-3 text-[12px] ${muted}`}>Select a person to edit claims.</p> : null}
            {claims.length && !visibleClaims.length ? <p className={`px-1 py-3 text-[12px] ${muted}`}>No claims match this search.</p> : null}
          </div>
        </aside>

        <section className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className={`shrink-0 border-b ${line}`}>
            <div className="flex items-center justify-between gap-3 px-4 pt-3">
              <div>
                <p className={`text-[13px] font-medium ${ink}`}>Starting query</p>
                <p className={`text-[11px] ${muted}`}>Single-table SELECT. Claims bind before the query is rewritten.</p>
              </div>
              <CopyButton isLight={isLight} value={query} label="Copy" />
            </div>
            <textarea
              className={`mt-2 max-h-40 min-h-[7.5rem] w-full resize-y overflow-auto border-t px-4 py-2.5 font-mono text-[12px] leading-5 focus:outline-none ${line} ${
                isLight ? 'bg-[#FBFCFD] text-[#1C1C1C] placeholder:text-[#9AA1AB]' : 'bg-black text-slate-100 placeholder:text-slate-500'
              }`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              spellCheck={false}
              onKeyDown={(event) => {
                if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                  event.preventDefault();
                  void run();
                }
              }}
            />
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            <div className={`flex shrink-0 flex-wrap items-center justify-between gap-2 border-b px-4 py-2.5 ${line}`}>
              <div className="flex flex-wrap items-center gap-2">
                {result ? (
                  <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[12px] font-medium ${
                    result.allow
                      ? isLight ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
                      : isLight ? 'border-red-200 bg-red-50 text-red-700' : 'border-red-500/30 bg-red-500/10 text-red-200'
                  }`}
                  >
                    {result.allow ? <ShieldCheck size={13} /> : <ShieldX size={13} />}
                    {result.allow ? 'Allowed' : 'Denied'}
                  </span>
                ) : (
                  <p className={`text-[13px] font-medium ${ink}`}>Results</p>
                )}
                {result ? (
                  <span className={`text-[11px] ${muted}`}>
                    {result.applies ? 'Policy applied' : 'Policy did not apply'}
                    {result.denyReason ? ` · ${result.denyReason}` : ''}
                  </span>
                ) : (
                  <p className={`text-[11px] ${muted}`}>Run test to compile, rewrite, and sample rows.</p>
                )}
              </div>
              {result ? (
                <div className={`inline-flex rounded-md border p-0.5 ${line} ${isLight ? 'bg-white' : 'bg-black'}`}>
                  {([
                    ['trace', 'Trace'],
                    ['queries', 'Queries'],
                    ['rows', 'Rows'],
                  ] as const).map(([id, label]) => (
                    <button
                      key={id}
                      type="button"
                      className={`h-7 rounded px-2.5 text-[11px] font-medium ${
                        pane === id
                          ? isLight ? 'bg-[#E7F2FB] text-[#16324F]' : 'bg-white/10 text-white'
                          : muted
                      }`}
                      onClick={() => setPane(id)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            {error ? (
              <div className={`mx-4 mt-3 shrink-0 rounded-md border px-3 py-2 text-[12px] ${isLight ? 'border-red-200 bg-red-50 text-red-700' : 'border-red-500/30 bg-red-500/10 text-red-200'}`}>
                {error}
              </div>
            ) : null}

            {busy && !result ? (
              <div className={`flex min-h-0 flex-1 flex-col items-center justify-center gap-2 ${muted}`}>
                <Loader2 size={18} className="animate-spin" />
                <p className="text-[13px]">Running policy test…</p>
              </div>
            ) : null}

            {!result && !busy ? (
              <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-6 text-center">
                <div className={`mb-3 inline-flex h-10 w-10 items-center justify-center rounded-md border ${line} ${isLight ? 'bg-white text-[#4B5563]' : 'bg-white/5 text-slate-300'}`}>
                  <Play size={16} />
                </div>
                <p className={`text-[14px] font-medium ${ink}`}>Ready to test the saved policy</p>
                <p className={`mt-1 max-w-md text-[12px] leading-5 ${muted}`}>
                  Pick a person, review claims, then run the test. You will see how the policy compiles, how the query is rewritten, and which rows come back.
                </p>
              </div>
            ) : null}

            {result && pane === 'trace' ? (
              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
                <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Metric isLight={isLight} label="Row filters" value={String(result.applied.rowFilters)} />
                  <Metric isLight={isLight} label="Column masks" value={String(result.applied.columnMasks.length)} />
                  <Metric isLight={isLight} label="Rows" value={result.allow ? String(result.rowCount) : '—'} />
                  <Metric isLight={isLight} label="Steps" value={String(result.steps.length)} />
                </div>
                <ol className="space-y-2 pb-2">
                  {result.steps.map((step, index) => (
                    <TraceStep key={step.id || `${step.title}-${index}`} isLight={isLight} index={index} step={step} />
                  ))}
                </ol>
              </div>
            ) : null}

            {result && pane === 'queries' ? (
              <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-hidden p-4 xl:grid-cols-2">
                <SqlBlock isLight={isLight} title="Original query" value={result.originalQuery} />
                <SqlBlock isLight={isLight} title="Rewritten query" value={result.rewrittenQuery || (result.allow ? result.originalQuery : 'Query was not rewritten.')} />
              </div>
            ) : null}

            {result && pane === 'rows' ? (
              result.allow ? (
                result.rows.length ? (
                  <div className="min-h-0 flex-1 overflow-auto">
                    <table className="min-w-full border-collapse text-left text-[12px]">
                      <thead className={`sticky top-0 z-10 ${isLight ? 'bg-[#F7F8FA] text-[#5C6570]' : 'bg-[#111] text-slate-400'}`}>
                        <tr>
                          {resultColumns.map((column) => (
                            <th key={column.name} className={`whitespace-nowrap border-b px-3 py-2 font-mono font-medium ${line}`}>
                              <span className="block">{column.name}</span>
                              {column.type ? <span className={`block text-[10px] font-normal ${muted}`}>{column.type}</span> : null}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className={ink}>
                        {result.rows.map((row, index) => (
                          <tr key={index} className={isLight ? (index % 2 ? 'bg-[#FAFBFC]' : 'bg-white') : index % 2 ? 'bg-white/[0.02]' : ''}>
                            {resultColumns.map((column) => {
                              const display = formatPreviewCell(row[column.name]);
                              return (
                                <td key={column.name} title={display} className={`max-w-[16rem] truncate whitespace-nowrap border-b px-3 py-1.5 font-mono text-[11px] ${line}`}>
                                  {display}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className={`border-t px-4 py-2 text-[11px] ${line} ${muted}`}>
                      {result.rowCount} row{result.rowCount === 1 ? '' : 's'}
                      {result.truncated ? ' · truncated' : ''}
                      {result.applied.columnMasks.length ? ` · masked ${result.applied.columnMasks.join(', ')}` : ''}
                    </p>
                  </div>
                ) : (
                  <div className={`flex min-h-0 flex-1 flex-col items-center justify-center px-6 text-center ${muted}`}>
                    <Table2 size={18} className="mb-2" />
                    <p className={`text-[13px] font-medium ${ink}`}>No rows returned</p>
                    <p className="mt-1 max-w-sm text-[12px]">The rewritten query ran, but nothing came back for this person and claims.</p>
                  </div>
                )
              ) : (
                <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-6 text-center">
                  <ShieldX size={18} className={isLight ? 'mb-2 text-red-600' : 'mb-2 text-red-300'} />
                  <p className={`text-[13px] font-medium ${ink}`}>Query did not run</p>
                  <p className={`mt-1 max-w-sm text-[12px] ${muted}`}>{result.denyReason || 'The policy denied this request.'}</p>
                </div>
              )
            ) : null}
          </div>
        </section>
      </div>
    </DialogShell>
  );
}

function TraceStep({
  isLight,
  index,
  step,
}: {
  isLight: boolean;
  index: number;
  step: PolicyTestStep;
}) {
  const muted = isLight ? 'text-[#6B7280]' : 'text-slate-400';
  const ink = isLight ? 'text-[#1C1C1C]' : 'text-white';
  const stage = stepStage(step.id);
  return (
    <li className={`rounded-md border px-3 py-2.5 ${isLight ? 'border-[#E6E8EB] bg-white' : 'border-white/10 bg-white/[0.03]'}`}>
      <div className="flex items-start gap-2.5">
        <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-[10px] font-semibold ${isLight ? 'bg-[#F2F4F7] text-[#3C4048]' : 'bg-white/10 text-slate-200'}`}>
          {index + 1}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className={`text-[12px] font-medium ${ink}`}>{step.title}</p>
            <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${isLight ? 'bg-[#F2F4F7] text-[#5C6570]' : 'bg-white/10 text-slate-300'}`}>{stage}</span>
          </div>
          {step.detail ? (
            <pre className={`mt-1.5 max-h-40 overflow-auto whitespace-pre-wrap font-mono text-[11px] leading-5 ${muted}`}>{step.detail}</pre>
          ) : null}
        </div>
      </div>
    </li>
  );
}
